import { ListingStatusEnum } from '../../../../../types/listingStatus'

export interface MarkerColorConfig {
  backgroundColor: string
  borderColor: string
  textColor: string
}

export interface ThemeColors {
  isLightTheme: boolean
}

export interface MarkerState {
  status: ListingStatusEnum
  isViewed: boolean
  isHovered: boolean
  isSelected: boolean
  isActive: boolean
}

// Cache for base colors to avoid recalculating the same status/theme combinations
const baseColorsCache = new Map<string, MarkerColorConfig>()

/**
 * Clear the base colors cache - useful when theme changes
 */
export const clearMarkerColorsCache = (): void => {
  baseColorsCache.clear()
}

/**
 * Get marker colors based on listing status, theme, and state
 */
export const getMarkerColors = (state: MarkerState, theme: ThemeColors): MarkerColorConfig => {
  const { status, isViewed, isHovered, isSelected, isActive } = state
  const { isLightTheme } = theme

  // Selected state takes precedence
  if (isSelected) {
    return {
      backgroundColor: 'var(--brand-primary-400)',
      borderColor: 'var(--brand-primary-border-300)',
      textColor: isLightTheme ? 'var(--brand-neutral-cool-900)' : 'var(--brand-hb-white)'
    }
  }

  // Base colors by status
  const baseColors = getBaseColorsByStatus(status, isLightTheme)

  // Apply active/hover state modifications (active takes precedence over hover)
  const hoverOrActiveColors = isActive || isHovered ? getHoverColors(baseColors, isLightTheme, status) : baseColors

  // Apply viewed state modifications (only if not hovered or active)
  const finalColors =
    isViewed && !isHovered && !isActive ? getViewedColors(baseColors, isLightTheme) : hoverOrActiveColors

  return finalColors
}

/**
 * Get base colors for each listing status with caching
 */
const getBaseColorsByStatus = (status: ListingStatusEnum, isLightTheme: boolean): MarkerColorConfig => {
  // Create cache key from status and theme
  const cacheKey = `${status}-${isLightTheme}`

  // Check cache first
  if (baseColorsCache.has(cacheKey)) {
    return baseColorsCache.get(cacheKey)!
  }

  // Calculate colors based on status
  let colors: MarkerColorConfig

  switch (status) {
    case ListingStatusEnum.ACTIVE:
    case ListingStatusEnum.COMING_SOON:
      colors = isLightTheme
        ? {
            backgroundColor: 'var(--brand-secondary-200)',
            borderColor: 'var(--brand-secondary-400)',
            textColor: 'var(--brand-neutral-cool-900)'
          }
        : {
            backgroundColor: 'var(--brand-secondary-200)',
            borderColor: 'var(--brand-secondary-300)',
            textColor: 'var(--brand-hb-white)'
          }
      break

    case ListingStatusEnum.PENDING:
    case ListingStatusEnum.ACTIVE_UNDER_CONTRACT:
      colors = isLightTheme
        ? {
            backgroundColor: 'var(--brand-secondary-accent-200)',
            borderColor: 'var(--brand-secondary-accent-400)',
            textColor: 'var(--brand-neutral-cool-900)'
          }
        : {
            backgroundColor: 'var(--brand-secondary-accent-200)',
            borderColor: 'var(--brand-secondary-accent-300)',
            textColor: 'var(--brand-hb-white)'
          }
      break

    case ListingStatusEnum.CLOSED:
    case ListingStatusEnum.CANCELLED:
    case ListingStatusEnum.EXPIRED:
    case ListingStatusEnum.WITHDRAWN:
    case ListingStatusEnum.INCOMPLETE:
    case ListingStatusEnum.DELETE:
    case ListingStatusEnum.UNKNOWN:
    case ListingStatusEnum.HOLD:
      colors = isLightTheme
        ? {
            backgroundColor: 'var(--brand-danger-200)',
            borderColor: 'var(--brand-danger-400)',
            textColor: 'var(--brand-neutral-cool-900)'
          }
        : {
            backgroundColor: 'var(--brand-danger-200)',
            borderColor: 'var(--brand-danger-300)',
            textColor: 'var(--brand-hb-white)'
          }
      break

    default:
      colors = isLightTheme
        ? {
            backgroundColor: 'var(--brand-secondary-200)',
            borderColor: 'var(--brand-secondary-400)',
            textColor: 'var(--brand-neutral-cool-900)'
          }
        : {
            backgroundColor: 'var(--brand-secondary-200)',
            borderColor: 'var(--brand-secondary-300)',
            textColor: 'var(--brand-hb-white)'
          }
  }

  // Cache the result
  baseColorsCache.set(cacheKey, colors)
  return colors
}

/**
 * Apply viewed state modifications
 */
const getViewedColors = (baseColors: MarkerColorConfig, isLightTheme: boolean): MarkerColorConfig => {
  return {
    ...baseColors,
    backgroundColor: isLightTheme
      ? baseColors.backgroundColor.replace('200', '50')
      : baseColors.backgroundColor.replace('200', '100'),
    borderColor: isLightTheme
      ? baseColors.borderColor.replace('400', '200')
      : baseColors.borderColor.replace('300', '200')
  }
}

/**
 * Apply hover state modifications (darker/more saturated colors)
 */
const getHoverColors = (
  baseColors: MarkerColorConfig,
  isLightTheme: boolean,
  status: ListingStatusEnum
): MarkerColorConfig => {
  // Special handling for PENDING and ACTIVE_UNDER_CONTRACT statuses, we wanted it a little darker than the default
  if (status === ListingStatusEnum.PENDING || status === ListingStatusEnum.ACTIVE_UNDER_CONTRACT) {
    return {
      ...baseColors,
      backgroundColor: isLightTheme
        ? baseColors.backgroundColor.replace('200', '400')
        : baseColors.backgroundColor.replace('200', '500'),
      borderColor: isLightTheme
        ? baseColors.borderColor.replace('400', '600')
        : baseColors.borderColor.replace('300', '700')
    }
  }

  // Default hover behavior for other statuses
  return {
    ...baseColors,
    backgroundColor: isLightTheme
      ? baseColors.backgroundColor.replace('200', '400')
      : baseColors.backgroundColor.replace('200', '600'),
    borderColor: isLightTheme
      ? baseColors.borderColor.replace('400', '600')
      : baseColors.borderColor.replace('300', '500')
  }
}
